import { useState } from 'react';
import { Plus, Check, X, Info, AlertCircle } from 'lucide-react';
import {
  useLeaveRequests,
  useApproveLeave,
  useRejectLeave,
  useCancelLeave,
  useCreateLeave,
  useMyBalance,
} from '@/lib/queries';
import { useAuthStore } from '@/store/auth.store';

const TYPE_LABELS: Record<string, string> = {
  VACATION: 'Vacaciones',
  MEDICAL: 'Permiso médico',
  PERSONAL: 'Permiso personal',
  BEREAVEMENT: 'Duelo',
  MATERNITY: 'Maternidad',
  PATERNITY: 'Paternidad',
  STUDY: 'Estudios',
  REMOTE: 'Trabajo remoto',
  OTHER: 'Otro',
};

const STATUS_STYLES: Record<string, string> = {
  PENDING: 'bg-yellow-50 text-warning',
  APPROVED: 'bg-accent-light text-accent',
  REJECTED: 'bg-red-50 text-error',
  CANCELLED: 'bg-gray-100 text-gray-600',
  ESCALATED: 'bg-orange-50 text-orange-600',
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobado',
  REJECTED: 'Rechazado',
  CANCELLED: 'Cancelado',
  ESCALATED: 'Escalado',
};

export function LeavePage() {
  const user = useAuthStore((s) => s.user);
  const isApprover =
    user?.role === 'ADMIN' || user?.role === 'HR' || user?.role === 'MANAGER';

  const [tab, setTab] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [showForm, setShowForm] = useState(false);

  const { data: all } = useLeaveRequests({});
  const { data: balance } = useMyBalance();

  const items =
    tab === 'pending'
      ? all?.items?.filter((l: any) => l.status === 'PENDING')
      : tab === 'approved'
      ? all?.items?.filter((l: any) => l.status === 'APPROVED')
      : tab === 'rejected'
      ? all?.items?.filter((l: any) => l.status === 'REJECTED')
      : all?.items;

  const percentUsed = balance
    ? Math.round(((balance.taken ?? 0) / (balance.generated || 1)) * 100)
    : 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-font-primary">Permisos y Vacaciones</h1>
          <p className="mt-1 text-sm text-font-secondary">
            Gestiona tus solicitudes y aprobaciones
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white shadow-sm hover:bg-primary-dark"
        >
          <Plus size={16} /> Nueva solicitud
        </button>
      </div>

      {/* Balance + Próxima ausencia */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="col-span-2 rounded-lg border border-border bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-semibold text-font-primary">
              Saldo de Vacaciones
            </h2>
            <span className="text-xs text-font-tertiary">Año 2026</span>
          </div>
          <div className="flex items-end gap-3">
            <span className="text-5xl font-bold text-primary">
              {balance?.available ?? 15}
            </span>
            <span className="mb-2 text-sm font-medium text-font-secondary">
              días disponibles
            </span>
          </div>
          <div className="mt-5">
            <div className="mb-2 flex justify-between text-xs text-font-secondary">
              <span>{percentUsed}% utilizados</span>
              <span>
                {balance?.taken ?? 0} / {balance?.generated ?? 15} días
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-border-light">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary to-primary-light"
                style={{ width: `${percentUsed}%` }}
              />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-white p-6 shadow-sm">
          <h2 className="text-base font-semibold text-font-primary">Próxima Ausencia</h2>
          <p className="mt-4 text-xl font-bold text-font-primary">14 - 16 Abril, 2026</p>
          <p className="mt-1 text-sm text-font-secondary">Vacaciones · 3 días</p>
          <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-accent-light px-2 py-0.5 text-[11px] font-semibold text-accent">
            <span className="h-1.5 w-1.5 rounded-full bg-current" /> Aprobado
          </span>
        </div>
      </div>

      {/* Tabs + Tabla */}
      <div className="rounded-lg border border-border bg-white shadow-sm">
        <div className="flex gap-1 border-b border-border px-6">
          {(['all', 'pending', 'approved', 'rejected'] as const).map((t) => {
            const labels = {
              all: 'Todas',
              pending: 'Pendientes',
              approved: 'Aprobadas',
              rejected: 'Rechazadas',
            };
            return (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`relative px-4 py-4 text-sm font-semibold transition ${
                  tab === t ? 'text-primary' : 'text-font-secondary hover:text-font-primary'
                }`}
              >
                {labels[t]}
                {tab === t && (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-t bg-primary" />
                )}
              </button>
            );
          })}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-font-secondary">
                <th className="px-6 py-3">Tipo</th>
                <th className="px-4 py-3">Fechas</th>
                <th className="px-4 py-3">Días</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Aprobador</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((lr: any) => (
                <LeaveRow
                  key={lr.id}
                  lr={lr}
                  canApprove={
                    isApprover &&
                    (lr.status === 'PENDING' || lr.status === 'ESCALATED')
                  }
                />
              ))}
              {(!items || items.length === 0) && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-font-secondary">
                    Sin solicitudes
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <LeaveFormModal onClose={() => setShowForm(false)} />}
    </div>
  );
}

function LeaveRow({ lr, canApprove }: { lr: any; canApprove: boolean }) {
  const approve = useApproveLeave();
  const reject = useRejectLeave();
  const cancel = useCancelLeave();
  const user = useAuthStore((s) => s.user);

  const handleReject = async () => {
    const reason = prompt('Motivo del rechazo:');
    if (!reason) return;
    await reject.mutateAsync({ id: lr.id, reason });
  };

  const isMine = lr.employeeId === user?.employeeId;
  const canCancel = isMine && (lr.status === 'PENDING' || lr.status === 'APPROVED');
  const fmt = (d: string) =>
    new Date(d).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <tr className="border-b border-border-light hover:bg-bg">
      <td className="px-6 py-4 font-medium text-font-primary">
        {TYPE_LABELS[lr.type] ?? lr.type}
      </td>
      <td className="px-4 py-4 text-font-secondary">
        {fmt(lr.startDate)} → {fmt(lr.endDate)}
      </td>
      <td className="px-4 py-4 font-semibold text-font-primary">{lr.workingDays}</td>
      <td className="px-4 py-4">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            STATUS_STYLES[lr.status] ?? 'bg-gray-100 text-gray-600'
          }`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {STATUS_LABELS[lr.status] ?? lr.status}
        </span>
      </td>
      <td className="px-4 py-4 text-font-secondary">
        {lr.approver ? `${lr.approver.firstName} ${lr.approver.lastName}` : '–'}
      </td>
      <td className="px-4 py-4 text-right">
        {canApprove && (
          <div className="inline-flex gap-1">
            <button
              onClick={() => approve.mutate(lr.id)}
              className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-light text-accent hover:bg-accent hover:text-white"
              title="Aprobar"
            >
              <Check size={14} />
            </button>
            <button
              onClick={handleReject}
              className="flex h-8 w-8 items-center justify-center rounded-md bg-red-50 text-error hover:bg-error hover:text-white"
              title="Rechazar"
            >
              <X size={14} />
            </button>
          </div>
        )}
        {canCancel && !canApprove && (
          <button
            onClick={() => cancel.mutate(lr.id)}
            className="text-xs font-semibold text-font-secondary hover:text-error"
          >
            Cancelar
          </button>
        )}
      </td>
    </tr>
  );
}

function LeaveFormModal({ onClose }: { onClose: () => void }) {
  const create = useCreateLeave();
  const [form, setForm] = useState({
    type: 'VACATION',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: new Date().toISOString().slice(0, 10),
    reason: '',
  });
  const [error, setError] = useState<string | null>(null);

  // calc days
  const start = new Date(form.startDate);
  const end = new Date(form.endDate);
  const days = Math.max(
    0,
    Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1,
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync(form);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error?.message ?? 'Error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-lg rounded-lg bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-bold text-font-primary">Nueva Solicitud</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-font-secondary hover:text-font-primary"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-col gap-5 p-6">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-font-primary">
              Tipo de solicitud
            </span>
            <select
              className="input h-10"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-font-primary">
                Fecha inicio
              </span>
              <input
                type="date"
                className="input h-10"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-font-primary">
                Fecha fin
              </span>
              <input
                type="date"
                className="input h-10"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              />
            </label>
          </div>

          <div className="flex items-start gap-2 rounded-md bg-primary-subtle p-3 text-xs text-primary">
            <Info size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              Se descontarán {days} día(s) laborales de tu saldo de vacaciones.
            </span>
          </div>

          <div className="flex items-start gap-2 rounded-md bg-yellow-50 p-3 text-xs text-warning">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              Nota: Si tu solicitud supera 15 días laborales, será escalada
              automáticamente.
            </span>
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-font-primary">
              Motivo (opcional)
            </span>
            <textarea
              className="input"
              rows={3}
              placeholder="Vacaciones familiares planificadas"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </label>

          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-error">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-border bg-bg px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 items-center gap-2 rounded-md border border-border bg-white px-4 text-sm font-semibold text-font-primary hover:bg-bg"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={create.isPending}
            className="flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-60"
          >
            Enviar solicitud
          </button>
        </div>
      </form>
    </div>
  );
}
